'use client';

import { type ComponentPropsWithRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

const switchVariants = cva(
    `[&_span]:shadow-none peer inline-flex h-6 w-[48px] shrink-0 items-center rounded-full
     cursor-pointer border-2 border-transparent shadow-none transition-colors
     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background
     disabled:cursor-not-allowed`,
    {
        variants: {
            variant: {
                default: 'data-[state=checked]:bg-primary data-[state=unchecked]:bg-snk-keyline-grey',
                color: 'data-[state=unchecked]:bg-destructive-foreground data-[state=checked]:bg-success-foreground',
            },
            isDisabled: {
                false: '',
                true: '!bg-snk-disabled disabled:opacity-100',
            },
        },
        defaultVariants: {
            variant: 'default',
            isDisabled: false,
        },
    }
);

const labelVariants = cva(
    'pointer-events-none absolute inset-0 flex items-center gap-0 p-1.5 text-[8px] leading-none uppercase tracking-[0.16px] peer-data-[state=checked]:justify-start peer-data-[state=unchecked]:justify-end',
    {
        variants: {
            variant: {
                default:
                    'peer-data-[state=unchecked]:text-foreground peer-data-[state=checked]:text-primary-foreground',
                color: '!text-primary',
            },
            isDisabled: {
                false: '',
                true: '!text-muted-foreground',
            },
        },
        defaultVariants: {
            variant: 'default',
            isDisabled: false,
        },
    }
);

export interface ToggleProps
    extends Omit<ComponentPropsWithRef<typeof Switch>, 'checked' | 'children' | 'defaultChecked' | 'onCheckedChange'>,
        VariantProps<typeof switchVariants> {
    checked: boolean;
    hasLabel?: boolean;
    onCheckedChange: (checked: boolean) => void;
}

export const Toggle = ({
    checked,
    className,
    variant,
    hasLabel = false,
    disabled = false,
    ref,
    ...props
}: ToggleProps) => {
    const isChecked = checked;

    return (
        <div className="relative inline-flex items-center">
            <Switch
                checked={checked}
                disabled={disabled}
                ref={ref}
                className={cn(switchVariants({ variant, isDisabled: disabled }), className)}
                {...props}
            />
            {hasLabel ? (
                <Label
                    htmlFor={props.id}
                    aria-hidden="true"
                    className={cn(labelVariants({ variant, isDisabled: disabled }))}>
                    {isChecked ? 'On' : 'Off'}
                </Label>
            ) : null}
        </div>
    );
};
